import { useUpdateSubscription } from "@/api/stripe";
import { useUserInfo } from "@/api/user";
import { ROUTES } from "@/constants/routes";
import Sentry from "@/integrations/Sentry";
import {
  trackBillingCheckoutCompleted,
  trackBillingCheckoutFailed,
} from "@/utils/analytics";
import type { BillingInterval } from "@cohesive/api-client";
import { ErrorDisplay, Spinner } from "@cohesive/ui-components";
import { useStripe } from "@stripe/react-stripe-js";
import type { Stripe } from "@stripe/stripe-js";
import { useMutation } from "@tanstack/react-query";

import React, { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

interface ConfirmSetupParams {
  stripe: Stripe;
  setupIntentClientSecret: string;
  planName: string | null;
  billingCycle: BillingInterval | null;
  userCount: number;
  isPaymentMethodUpdate: boolean;
  keepTrial: boolean;
  redirectTo: string | null;
  hasExistingSubscription: boolean;
  existingSubscriptionStatus: string | undefined;
}

export const ConfirmSetupPage: React.FC = () => {
  const stripe = useStripe();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const updateSubscription = useUpdateSubscription();

  // Use userInfo to determine if the user already has a subscription
  // This is more reliable than calling getSubscription which may 404
  const { data: userInfo, isLoading: isUserInfoLoading } = useUserInfo();

  // Check if user has an existing subscription from /me endpoint
  const existingSubscription = userInfo?.subscription;
  const hasExistingSubscription = !!existingSubscription?.id;

  // Create a mutation for the confirm setup flow
  // This ensures the logic only runs once and provides proper loading/error states
  const confirmSetupMutation = useMutation({
    mutationKey: ["confirmSetup", searchParams.get("setup_intent")],
    mutationFn: async (params: ConfirmSetupParams) => {
      const {
        stripe,
        setupIntentClientSecret,
        planName,
        billingCycle,
        userCount,
        keepTrial,
        hasExistingSubscription,
        existingSubscriptionStatus,
      } = params;

      // Retrieve the setup intent from Stripe
      const response = await stripe.retrieveSetupIntent(
        setupIntentClientSecret,
      );

      // Extract payment method ID
      let paymentMethodId: string | null = null;
      if (typeof response.setupIntent?.payment_method === "string") {
        paymentMethodId = response.setupIntent.payment_method;
      } else if (
        typeof response.setupIntent?.payment_method === "object" &&
        response.setupIntent?.payment_method?.id
      ) {
        paymentMethodId = response.setupIntent.payment_method.id;
      }

      if (!paymentMethodId) {
        throw new Error("Invalid payment method");
      }

      // Determine if this was a trial subscription
      const wasTrialing = existingSubscriptionStatus === "trialing";

      // Update or create subscription with the payment method
      if (planName && billingCycle && userCount) {
        const response = await updateSubscription.mutateAsync({
          plan_name: planName,
          billing_interval: billingCycle,
          seat_count: userCount,
          default_payment_method_id: paymentMethodId,
          // Only end trial if there was an existing trial subscription
          // For new subscriptions, the backend will create a trial.
          // keep_trial checkouts attach a card to a payment-method-required
          // trial without forfeiting the remaining trial days.
          trial_end_now: wasTrialing && hasExistingSubscription && !keepTrial,
        });

        return response;
      }

      return { wasTrialing };
    },
    onSuccess: (data, params) => {
      const {
        planName,
        billingCycle,
        userCount,
        isPaymentMethodUpdate,
        redirectTo,
      } = params;

      // Track successful checkout completion
      trackBillingCheckoutCompleted({
        planName: planName || undefined,
        billingInterval: billingCycle || undefined,
        seatCount: userCount || undefined,
        isPaymentMethodUpdate,
        // @ts-ignore
        wasTrialing: data.wasTrialing,
      });

      // Navigate to specified redirect or default to HOME
      navigate(redirectTo || ROUTES.HOME, { replace: true });
    },
    onError: (error: Error, params) => {
      const { planName, billingCycle, userCount, isPaymentMethodUpdate } =
        params;

      // Track checkout failure
      trackBillingCheckoutFailed({
        planName: planName || undefined,
        billingInterval: billingCycle || undefined,
        seatCount: userCount || undefined,
        error: error.message,
        isPaymentMethodUpdate,
      });

      Sentry.captureException(error);
    },
  });

  // Parse search params once
  const setupIntent = searchParams.get("setup_intent");
  const setupIntentClientSecret = searchParams.get(
    "setup_intent_client_secret",
  );
  const isPaymentMethodUpdate =
    searchParams.get("mode") === "payment_method_update";
  const keepTrial = searchParams.get("keep_trial") === "1";
  const redirectTo = searchParams.get("redirect_to");
  const planName = searchParams.get("plan");
  const billingCycle = searchParams.get(
    "billing_cycle",
  ) as BillingInterval | null;
  const userCount = parseInt(searchParams.get("user_count") || "1");

  // Trigger the mutation once when all required data is available
  useEffect(() => {
    // Don't proceed until we have the required data
    if (
      !stripe ||
      !setupIntent ||
      !setupIntentClientSecret ||
      isUserInfoLoading
    ) {
      return;
    }

    // Don't trigger if mutation is already in progress, succeeded, or failed
    if (
      confirmSetupMutation.isPending ||
      confirmSetupMutation.isSuccess ||
      confirmSetupMutation.isError
    ) {
      return;
    }

    // Trigger the mutation
    confirmSetupMutation.mutate({
      stripe,
      setupIntentClientSecret,
      planName,
      billingCycle,
      userCount,
      isPaymentMethodUpdate,
      keepTrial,
      redirectTo,
      hasExistingSubscription,
      existingSubscriptionStatus: existingSubscription?.status,
    });
  }, [
    stripe,
    setupIntent,
    setupIntentClientSecret,
    isUserInfoLoading,
    confirmSetupMutation.isPending,
    confirmSetupMutation.isSuccess,
    confirmSetupMutation.isError,
  ]);

  if (confirmSetupMutation.isError) {
    const title = "Error";
    return (
      <>
        <title>{title}</title>
        <div data-testid="error-message">
          <ErrorDisplay
            title={title}
            message={confirmSetupMutation.error.message}
          />
        </div>
      </>
    );
  }

  const title = isPaymentMethodUpdate
    ? "Updating Payment Method"
    : "Confirming Your Purchase";

  return (
    <>
      <title>{title}</title>
      <div className="flex items-center justify-center h-full">
        <Spinner size="xl" />
      </div>
    </>
  );
};
